<script setup lang="ts">
import type { AlgoliaSizeFilterProps } from './AlgoliaSizeFilter.props'
const props = defineProps<AlgoliaSizeFilterProps>()
const { items } = toRefs(props)

const { getSizes, countries, categories } = await useSizeRetrieveData()

const sizes = await getSizes(items.value.map(item => item.value))
if (!sizes) {
  throw new Error('Sizes not found')
}

const { getSizeTabs } = useSizeUtils(sizes, countries, categories)

const tabs = ref(getSizeTabs(items.value))
watch(
  () => items.value,
  newVal => (tabs.value = getSizeTabs(newVal))
)

const { country } = useRouteHelper()
const selectedTabIndex = ref(0)

watch(
  () => items.value,
  newVal => {
    tabs.value = getSizeTabs(newVal)
    const foundIndex = tabs.value.findIndex(
      tab => tab.country.toLowerCase() === country.toLowerCase()
    )
    // findIndex method returns the index of the first element in the array where predicate is true, and -1 otherwise
    // if not found return first tab, if tab.country matches country returns matching tab
    selectedTabIndex.value = foundIndex === -1 ? 0 : foundIndex
  },
  {
    immediate: true,
  }
)
// Composables needed to calculate move and select the right shortcut and handle the transition
const { slider, handleShortcutClick, updateSliderPosition } =
  useSliderTransition(selectedTabIndex.value, false)
onMounted(() => {
  updateSliderPosition(selectedTabIndex.value)
  window.addEventListener('resize', handleResize)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
const handleResize = () => {
  updateSliderPosition(selectedTabIndex.value)
}
</script>

<template>
  <UtilsTabs
    v-model="selectedTabIndex"
    class-buttons-container="flex slider-tab"
    :tabs="tabs"
  >
    <template #button="{ tab, selected, index }">
      <UITabElement
        class="min-w-6xl shortcut-element"
        :class="{ 'z-10': selected }"
        :selected="selected"
        @click="handleShortcutClick(index)"
      >
        {{ tab.country }}
      </UITabElement>
    </template>
    <template #tabs-sufix>
      <div ref="slider" class="slider-tab-border" />
    </template>
    <template #panel="{ tab }">
      <div class="pt-6" data-testid="size-option-list">
        <UIDropdown
          v-for="category in tab.categories"
          :key="`${category}_${attribute}`"
          :default-open="true"
          :title="category.name"
          :custom-title-style="'text-book-7 uppercase'"
          :plus-icon="true"
          :icon-left="true"
        >
          <ul
            class="mb-4 grid grid-flow-col grid-cols-2 gap-3"
            :style="{
              gridTemplateRows: `repeat(${Math.ceil(category.options.length / 2)}, minmax(0, 1fr))`,
            }"
          >
            <li
              v-for="item in category.options"
              :key="`${item.value}_${attribute}`"
              class="relative flex gap-1"
            >
              <AtomsCheckbox
                v-model="item.isRefined"
                :label="item.label"
                @refine="refine(item.value)"
              >
                <template #label>
                  <span> {{ item.label }}</span>
                  <span
                    v-if="item?.count"
                    class="text-light-7 text-primitives-grey-200 mb-1 flex w-fit items-center"
                  >
                    ({{ item.count }})
                  </span>
                </template>
              </AtomsCheckbox>
            </li>
          </ul>
        </UIDropdown>
      </div>
    </template>
  </UtilsTabs>
</template>
